import { useCallback, useEffect, useState } from 'react';
import { Percent, UserPlus, X, RefreshCw } from 'lucide-react';
import { apiClient } from '../lib/api';
import { toast } from '../lib/toast';

interface AdminOverrideRow {
  _id: string;
  scope?: 'account' | 'supervisor';
  source?: 'am' | 'house';
  targetName?: string;
  accountName?: string;
  recipientName: string;
  attributedDistributorName: string;
  kind: 'permanent' | 'temporary';
  rate: number;
  startDate: string;
  endDate?: string;
  status: 'active' | 'ended' | 'cancelled';
  note?: string;
}

const statusStyles: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  ended: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-700'
};

// The two things an admin most needs to read off a row at a glance: what got
// tagged, and whose money pays for it.
const scopeLabels: Record<string, string> = {
  account: 'Clinic',
  supervisor: 'Supervisor'
};

const sourceStyles: Record<string, string> = {
  am: 'bg-sky-100 text-sky-800',
  house: 'bg-violet-100 text-violet-800'
};

const sourceLabels: Record<string, string> = {
  am: 'AM commission',
  house: 'House margin'
};

// House overrides apply to the floor-catalog price, AM overrides to the AM's
// markup — the same "20%" means different dollars, so the rate is never shown bare.
const basisLabels: Record<string, string> = {
  am: 'of AM commission',
  house: 'of floor price'
};

function fmtDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function CommissionOverrides() {
  const [rows, setRows] = useState<AdminOverrideRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [kindFilter, setKindFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showPartner, setShowPartner] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.getCommissionOverrides({
      status: statusFilter || undefined,
      kind: kindFilter || undefined,
      source: sourceFilter || undefined
    });
    setLoading(false);
    if (res.success) setRows((res as any).overrides || res.data?.overrides || []);
    else toast.error(res.error || 'Failed to load overrides');
  }, [statusFilter, kindFilter, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id: string, action: 'end' | 'cancel') => {
    setBusyId(id);
    const res = action === 'end'
      ? await apiClient.endCommissionOverride(id)
      : await apiClient.cancelCommissionOverride(id);
    setBusyId(null);
    if (res.success) { toast.success(`Override ${action === 'end' ? 'ended' : 'cancelled'}`); load(); }
    else toast.error(res.error || `Failed to ${action} override`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Percent className="text-indigo-600" size={22} />
          <h1 className="text-2xl font-semibold text-gray-900">Commission Overrides</h1>
        </div>
        <button
          onClick={() => setShowPartner(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
        >
          <UserPlus size={16} /> Add partner
        </button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Everything carrying a commission override. A <span className="font-medium">clinic</span> tag redirects
        a slice of the assigned AM&apos;s commission; a <span className="font-medium">supervisor</span> tag pays
        a percentage of the floor-catalog price out of the house margin on every order beneath that
        supervisor. Either can be permanent (lead-source partner) or temporary (departed rep). Overrides are
        added from the clinic&apos;s or supervisor&apos;s detail view; admins can end or cancel them here.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="ended">Ended</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={kindFilter} onChange={e => setKindFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="">All types</option>
          <option value="permanent">Permanent</option>
          <option value="temporary">Temporary</option>
        </select>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="">All sources</option>
          <option value="am">Out of AM commission</option>
          <option value="house">Out of house margin</option>
        </select>
        <button onClick={load} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">Target</th>
              <th className="px-4 py-2 font-medium">Comes out of</th>
              <th className="px-4 py-2 font-medium">Recipient</th>
              <th className="px-4 py-2 font-medium">Impacted AM</th>
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 font-medium">Rate</th>
              <th className="px-4 py-2 font-medium">Window</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">No overrides match the filter.</td></tr>
            ) : rows.map(row => {
              // Rows written before scope/source existed carry neither; they are
              // all clinic tags against the AM's commission.
              const scope = row.scope || 'account';
              const source = row.source || 'am';
              return (
              <tr key={row._id} className="hover:bg-gray-50">
                <td className="px-4 py-2">
                  <div className="font-medium text-gray-900">{row.targetName || row.accountName || '—'}</div>
                  <div className="text-xs text-gray-400">{scopeLabels[scope]}</div>
                </td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${sourceStyles[source]}`}>
                    {sourceLabels[source]}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-700">{row.recipientName}</td>
                <td className="px-4 py-2 text-gray-500">{row.attributedDistributorName}</td>
                <td className="px-4 py-2 capitalize text-gray-600">{row.kind}</td>
                <td className="px-4 py-2">
                  <span className="font-medium">{row.rate}%</span>
                  <span className="block text-xs text-gray-400">{basisLabels[source]}</span>
                </td>
                <td className="px-4 py-2 text-gray-500">
                  {fmtDate(row.startDate)}{row.kind === 'temporary' ? ` → ${fmtDate(row.endDate)}` : ''}
                </td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyles[row.status]}`}>{row.status}</span>
                </td>
                <td className="px-4 py-2 text-right">
                  {row.status === 'active' ? (
                    <div className="inline-flex items-center gap-2">
                      <button disabled={busyId === row._id} onClick={() => act(row._id, 'end')}
                        className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50">End</button>
                      <button disabled={busyId === row._id} onClick={() => act(row._id, 'cancel')}
                        className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50">Cancel</button>
                    </div>
                  ) : <span className="text-xs text-gray-300">—</span>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPartner && <AddPartnerModal onClose={() => setShowPartner(false)} />}
    </div>
  );
}

function AddPartnerModal({ onClose }: { onClose: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const valid = !!firstName.trim() && !!lastName.trim() && /.+@.+\..+/.test(email);

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    const res = await apiClient.createCommissionOverridePartner({
      firstName, lastName, email, companyName: companyName || undefined,
      paymentInstructions: paymentInstructions || undefined
    });
    setSubmitting(false);
    if (res.success) { toast.success('Partner created'); onClose(); }
    else toast.error(res.error || 'Failed to create partner');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-lg font-semibold text-gray-900">Add lead-source partner</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-500">
            A partner receives override payouts but never logs in. They become selectable as an override recipient.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
            <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Company (optional)"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <textarea value={paymentInstructions} onChange={e => setPaymentInstructions(e.target.value)}
            placeholder="Payout instructions (optional)" rows={2}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
        </div>
        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={submit} disabled={!valid || submitting}
            className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create partner'}
          </button>
        </div>
      </div>
    </div>
  );
}
